import { Storage, Auth, API, graphqlOperation } from '../utils/amplifyCompat'
import outputs from '../amplify_outputs.json'
import * as custom_queries from '../customgraphql/queries'
import * as custom_mutations from '../customgraphql/mutations'
import {
  createNotificationFromFollow,
  createNotificationThirtyJournal,
} from './notification'
import { reorderBooks } from '../utils/reorderBooks'
import {
  knownNativePushToken,
  pausePushTokenRegistration,
} from '../utils/nativePushToken'
import { alreadyExists } from '../utils/alreadyExists'
import { graphqlGetUser } from '../utils/getUserTolerant'

import moment from 'dayjs'

// Bucket of this Hosting branch's gen2 backend (the adopted Gen 1 bucket once
// postRefactorDone is deployed for the branch).
const bucket = outputs.storage.bucket_name
const bucketRegion = outputs.storage.aws_region

// Images are uploaded under the uploader's own public/<identity id>/ prefix:
// only that identity may write or delete there (G10), and the bucket's
// hand-managed public-read policy (public/* on production) serves them to
// everyone, signed out too. Keys carry a timestamp, so a replace is a new key.
// Older images keep their flat public/<key> URLs; nothing rewrites them.
export const ownPublicPath =
  (key) =>
  ({ identityId }) =>
    `public/${identityId}/${key}`

// Plain URL of an uploaded object, from the path Storage.put resolved (e.g.
// public/us-east-1:<identity id>/profile/<file>).
export function buildS3Url(path) {
  const encodedPath = path.split('/').map(encodeURIComponent).join('/')
  return `https://${bucket}.s3.${bucketRegion}.amazonaws.com/${encodedPath}`
}

function fetchingCurrentUserSession(
  isFetchingCurrentUserSession,
  isAuthenticated
) {
  return {
    type: 'SET_CURRENT_USER_SESSION',
    isFetchingCurrentUserSession,
    isAuthenticated,
  }
}

function setLoginSuccess(isLoginSuccess, accountData) {
  return {
    type: 'SET_LOGIN_SUCCESS',
    isLoginSuccess,
    accountData,
  }
}

// Get current User session
// -----------------------------------------------------------------------------
export function getCurrentUserSession() {
  return (dispatch) => {
    dispatch(fetchingCurrentUserSession(true, false))
    dispatch(setLoginSuccess(false, null))

    return new Promise((resolve, reject) => {
      Auth.currentSession()
        .then((user) => {
          dispatch(setLoginSuccess(true, user))
          dispatch(
            getUserData(
              user.idToken.payload['cognito:username'],
              null,
              user.idToken.payload.sub
            )
          )
          dispatch(fetchingCurrentUserSession(false, true))
          dispatch(getCurrentUser())
          return resolve(user)
        })
        .catch((err) => {
          if (err?.message !== 'No valid auth tokens') {
            console.log(err)
          }
          dispatch(fetchingCurrentUserSession(false, false))
          if (err?.message === 'No valid auth tokens') {
            return resolve(null)
          }
          return reject(err)
        })
    })
  }
}

// User Log in
// -----------------------------------------------------------------------------
export const userLoginRequest = (username, password) => {
  return (dispatch) => {
    dispatch(setLoginSuccess(false, null))
    dispatch({ type: 'SET_LOGIN_STATUS', loginError: null, loginLoading: true })

    callLoginAPI(username, password, (res) => {
      if (res instanceof Error) {
        dispatch({
          type: 'SET_LOGIN_STATUS',
          loginError: res,
          loginLoading: false,
        })
      } else {
        dispatch(setLoginSuccess(true, res))
        dispatch({
          type: 'SET_LOGIN_STATUS',
          loginError: null,
          loginLoading: false,
        })
        dispatch(
          getUserData(
            res.idToken.payload['cognito:username'],
            null,
            res.idToken.payload.sub
          )
        )
        dispatch(fetchingCurrentUserSession(false, true))
        dispatch(getCurrentUser())
      }
    })
  }
}

// Reset Log in error
// -----------------------------------------------------------------------------
export const resetLoginError = () => {
  return (dispatch) => {
    dispatch({ type: 'RESET_LOAGIN_ERROR' })
  }
}

// User Login HTTP request
// -----------------------------------------------------------------------------
function callLoginAPI(username, password, callback) {
  Auth.signIn(username, password)
    .then(async (user) => {
      // Tell React Native app that user successfully logged-in to the app and send username
      window.ReactNativeWebView &&
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ pushNotificationUsername: user.username })
        )
      return callback(user.signInUserSession)
    })
    .catch((err) => {
      return callback(new Error(err.message))
    })
}

// Create User data in DynamoDB on user signin for the first time
// -----------------------------------------------------------------------------
export function createUserData(username, uuid) {
  return async (dispatch) => {
    try {
      let user = await API.graphql(
        graphqlOperation(custom_mutations.createUser, {
          input: { id: username, uuid: uuid },
        })
      )
      // TODO: createUser error messege cause uuid passed in as undefined for the second time
      dispatch(
        getUserData(user.data.createUser.id, null, user.data.createUser.id)
      )
    } catch (err) {
      console.log('error creating user data: ', err)
    }
  }
}

// Get current user
// -----------------------------------------------------------------------------
export function getCurrentUser() {
  return async (dispatch) => {
    try {
      const user = await Auth.currentUserInfo()
      dispatch({ type: 'GET_CURRENT_USER', currentUser: user })
    } catch (err) {
      console.log('error creating user data: ', err)
    }
  }
}

// Update Profile Picture
// -----------------------------------------------------------------------------
export function updateProfilePic(id, file) {
  return async (dispatch) => {
    let user = await Auth.currentAuthenticatedUser()
    let userId = user.signInUserSession.idToken.payload['cognito:username']
    const extension = /^.+\.([^.]+)$/.exec(file.name)[1]
    const { type: mimeType } = file

    const dataToURL = new Date()
      .toJSON()
      .slice(0, 23)
      .replace(/[^0-9-]/g, '-')
    const filePath = `profile/${id}-${dataToURL}.${extension}`

    try {
      // Uploading to S3 bucket
      const { path } = await Storage.put(ownPublicPath(filePath), file, {
        contentType: mimeType,
      })
      const url = buildS3Url(path)

      // Adding URL to AWS Cognito
      let params = {}
      params['picture'] = url
      let result = await Auth.updateUserAttributes(user, params)
      await API.graphql(
        graphqlOperation(custom_mutations.updateUser, {
          input: { id: userId, profileImage: url },
        })
      )
      dispatch(getCurrentUser())
      return result
    } catch (err) {
      console.log('error updating profile picture: ', err)
    }
  }
}

// Update Article Pictures
// -----------------------------------------------------------------------------
export function uploadToS3(file, id) {
  return async (dispatch) => {
    let user = await Auth.currentAuthenticatedUser()
    let userId = user.signInUserSession.idToken.payload['cognito:username']
    const extension = /^.+\.([^.]+)$/.exec(file.name)[1]
    const { type: mimeType } = file

    const dataToURL = new Date().toJSON().slice(0, 10).replace(/-/g, '/')
    const filePath = `${dataToURL}/${userId}-article-${id}.${extension}`

    try {
      // Uploading to S3 bucket
      const { path } = await Storage.put(ownPublicPath(filePath), file, {
        contentType: mimeType,
      })

      return buildS3Url(path)
    } catch (err) {
      console.log('error uploading article picture: ', err)
    }
  }
}

// Get User Data
// -----------------------------------------------------------------------------
export function getUserData(username, limit, uuid) {
  return async (dispatch) => {
    try {
      dispatch({ type: 'SET_FETCH_USER_DATA_SESSION', isFetchUserData: true })
      const limitJournal = limit || 60
      const data = await graphqlGetUser(
        graphqlOperation(custom_queries.getUser, {
          id: username,
          limit: limitJournal,
        })
      )
      // console.log(data.data.getUser)

      if (!data.data.getUser) {
        if (!uuid) return
        dispatch(createUserData(username, uuid))
      } else {
        // This is main base data for logged in user and we are modifying;
        // User journals to group by month
        // and Book order by custom order that user set'

        // Grouping journals by month
        if (
          data.data.getUser.journals &&
          data.data.getUser.journals.items.length > 0
        ) {
          let groups = data.data.getUser.journals.items.reduce(function (r, o) {
            let m = o.createdAt.split('-')[0] + '-' + o.createdAt.split('-')[1]
            r[m]
              ? r[m].data.push(o)
              : (r[m] = {
                  month: moment(o.createdAt).format('MMM YYYY'),
                  data: [o],
                })
            return r
          }, {})

          // Add month as a key
          let journalByMonth = Object.keys(groups).map(function (k) {
            return groups[k]
          })

          // Replace journals data with new format with months
          data.data.getUser.journals.items = journalByMonth

          // reorder user books by bookOrder(users can change order or books)
          if (
            data.data.getUser.bookOrder &&
            data.data.getUser.bookOrder.length > 0
          ) {
            const newBookOrder = JSON.parse(data.data.getUser.bookOrder)
            data.data.getUser.books.items = reorderBooks(
              data.data.getUser.books.items,
              newBookOrder
            )
          }
        }

        dispatch({ type: 'GET_USER_DATA', userData: data.data.getUser })
        dispatch({
          type: 'SET_FETCH_USER_DATA_SESSION',
          isFetchUserData: false,
        })
      }
    } catch (err) {
      console.log('error getting user data: ', err)
      dispatch({ type: 'SET_FETCH_USER_DATA_SESSION', isFetchUserData: false })
    }
  }
}

// Get user journal data
// -----------------------------------------------------------------------------
export function getJournalsData(username, value, nextToken) {
  return async (dispatch) => {
    try {
      // TODO: search needs to be improved because query and filter only covers the data that are queried.
      const data = await graphqlGetUser(
        graphqlOperation(custom_queries.getJournals, {
          id: username,
          content: value,
          nextTokenJournal: nextToken,
        })
      )

      if (data.data.getUser.journals.items.length > 0) {
        let groups = data.data.getUser.journals.items.reduce(function (r, o) {
          let m = o.createdAt.split('-')[0] + '-' + o.createdAt.split('-')[1]
          r[m]
            ? r[m].data.push(o)
            : (r[m] = {
                month: moment(o.createdAt).format('MMM YYYY'),
                data: [o],
              })
          return r
        }, {})

        // Add month as a key
        let journalByMonth = Object.keys(groups).map(function (k) {
          return groups[k]
        })

        // Replace journals data with new format with months
        data.data.getUser.journals.items = journalByMonth
      } else {
        data.data.getUser.journals.items = ''
      }

      dispatch({
        type: nextToken ? 'GET_MORE_USER_SEARCH_DATA' : 'GET_USER_DATA',
        userData: data.data.getUser,
      })
    } catch (err) {
      console.log('error getting user data: ', err)
    }
  }
}

// get More user journal
// -----------------------------------------------------------------------------
export function getMoreUserJournal(username, nextToken) {
  return async (dispatch) => {
    try {
      const data = await graphqlGetUser(
        graphqlOperation(custom_queries.getUser, {
          id: username,
          limit: 60,
          nextTokenJournal: nextToken,
        })
      )
      // console.log(data.data.getUser)

      if (data.data.getUser.journals.items.length > 0) {
        let groups = data.data.getUser.journals.items.reduce(function (r, o) {
          let m = o.createdAt.split('-')[1]
          r[m]
            ? r[m].data.push(o)
            : (r[m] = {
                month: moment(o.createdAt).format('MMM YYYY'),
                data: [o],
              })
          return r
        }, {})

        // Add month as a key
        let journalByMonth = Object.keys(groups).map(function (k) {
          return groups[k]
        })

        dispatch({
          type: 'ADD_USER_JOURNAL_DATA',
          moreUserJournals: {
            items: journalByMonth,
            nextToken: data.data.getUser.journals.nextToken,
          },
        })
      } else {
        console.log('End of your journal')
        dispatch({
          type: 'ADD_USER_JOURNAL_DATA',
          moreUserJournals: { items: [], nextToken: null },
        })
      }
    } catch (err) {
      console.log('error getting more user journal data: ', err)
    }
  }
}

export function setMyJournalLimit(limit) {
  return (dispatch) => {
    dispatch({ type: 'SET_MY_JOURNAL_LIMIT', limit })
  }
}

// User Sign Out
// -----------------------------------------------------------------------------
export function userSignOut() {
  return async (dispatch) => {
    // In the mobile app, first take this device's push token off the account, while the
    // session is still valid (security sprint PR 11 follow-up).
    await unregisterNativePushToken()
    dispatch({
      type: 'SET_CURRENT_USER_SESSION',
      isAuthenticated: false,
      isFetchingCurrentUserSession: false,
    })
    Auth.signOut()
      .then((data) => {
        dispatch({ type: 'USER_LOGOUT' })
      })
      .catch((err) => console.log(err))
  }
}

// How long sign-out waits, in all, for a registration in flight and then the unregister call,
// before going ahead without them.
export const UNREGISTER_TIMEOUT_MS = 4000

// Resolves with `promise`, or rejects with { name: 'Timeout' } after `ms`.
function withTimeout(promise, ms) {
  let timer
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timer = setTimeout(() => reject({ name: 'Timeout' }), ms)
    }),
  ]).finally(() => clearTimeout(timer))
}

const unregisterReason = (err) =>
  err?.errors?.[0]?.errorType || err?.name || 'unknown'

// The username of the user-pool session this page holds now, or null.
async function sessionUsername() {
  try {
    const session = await Auth.currentSession()
    return (
      session.idToken.payload['cognito:username'] ||
      session.accessToken.payload.username ||
      null
    )
  } catch (err) {
    return null
  }
}

// Takes the mobile app's push token (if this page knows it) off the signed-in user's account,
// only if it is their current token (the server checks). Registration is paused first (a pending
// retry cancelled, new calls blocked, a call in flight waited for), so no registration lands
// after the unregister. Both steps share one UNREGISTER_TIMEOUT_MS budget, and the pause may use
// at most half of it, so the unregister itself still gets time; outside the WebView (no token,
// nothing in flight) this returns at once. Never throws; failures and timeouts are logged without
// the token, and sign-out goes ahead.
//
// A registration still in flight when the pause runs out may land after the unregister. If it
// then succeeds, the unregister is sent once more (not awaited), but only while the session is
// still the user who signed out: if someone else has signed in since (their session would take
// the token off their account) or nobody has, it is skipped. Limit: by then sign-out has usually
// cleared the session, so the second call rarely runs, and the token stays on the account until
// another account registers it on this device, the server finds it stale or dead, or the user
// signs out again.
// -----------------------------------------------------------------------------
export async function unregisterNativePushToken() {
  const deadline = Date.now() + UNREGISTER_TIMEOUT_MS
  const remaining = () => Math.max(0, deadline - Date.now())
  const expoNotificationToken = knownNativePushToken()
  // Who is signing out, read before anything is cleared (only needed with a token).
  const signingOut = expoNotificationToken
    ? await withTimeout(sessionUsername(), Math.min(remaining(), 1000)).catch(() => null)
    : null
  const paused = pausePushTokenRegistration()
  let pauseTimedOut = false
  try {
    await withTimeout(paused, Math.min(remaining(), UNREGISTER_TIMEOUT_MS / 2))
  } catch (err) {
    pauseTimedOut = true
    console.log('Push token registration still in flight at sign-out:', err?.name || 'unknown')
  }
  if (!expoNotificationToken) return
  const unregister = () =>
    API.graphql({
      query: custom_mutations.gnjAmplifyUnregisterPushToken,
      variables: { expoNotificationToken },
      authMode: 'AMAZON_COGNITO_USER_POOLS',
    })
  if (pauseTimedOut && signingOut) {
    paused
      .then(async (registered) => {
        if (!registered.some(Boolean)) return
        if ((await sessionUsername()) !== signingOut) return
        return unregister()
      })
      .catch((err) =>
        console.log('Late push token unregistration failed:', unregisterReason(err))
      )
  }
  try {
    await withTimeout(unregister(), remaining())
  } catch (err) {
    console.log('Push token unregistration failed:', unregisterReason(err))
  }
}

// Register the mobile app's push token on the signed-in user's account
// (src/utils/nativePushToken.js). Resolves with the GraphQL result ({ registered: false } when the
// server ignored it); throws on failure; the caller logs without the token.
// -----------------------------------------------------------------------------
export function registerPushToken(expoNotificationToken) {
  return API.graphql({
    query: custom_mutations.gnjAmplifyRegisterPushToken,
    variables: { expoNotificationToken },
    authMode: 'AMAZON_COGNITO_USER_POOLS',
  })
}

// New tokens for the signed-in user, so a retried registration carries an access token issued
// now (src/utils/nativePushToken.js).
export function refreshAuthSession() {
  return Auth.refreshSession()
}

// Get Author data
// -----------------------------------------------------------------------------
export function getAuthorData(username) {
  return async () => {
    try {
      const data = await API.graphql(
        graphqlOperation(custom_queries.getAuthor, { id: username })
      )

      if (
        data.data.getUser.bookOrder &&
        data.data.getUser.bookOrder.length > 0
      ) {
        const newBookOrder = JSON.parse(data.data.getUser.bookOrder)
        data.data.getUser.books.items = reorderBooks(
          data.data.getUser.books.items,
          newBookOrder
        )
      }

      return data.data.getUser
    } catch (err) {
      console.log('error getting author data: ')
      console.log(err)
    }
  }
}

// Get Author PUBLIC journal data
// -----------------------------------------------------------------------------
export function getAuthorPublicJournalData(journalAuthorId, nextToken) {
  return async () => {
    try {
      const data = await API.graphql({
        query: custom_queries.journalAuthorLatestAsKey,
        variables: {
          journalAuthorId: journalAuthorId,
          sortDirection: 'DESC',
          nextToken: nextToken,
          limit: 30,
        },
        authMode: 'AWS_IAM',
      })
      return data.data.journalAuthorLatestAsKey
    } catch (err) {
      console.log('error getting user data: ', err)
    }
  }
}

// Follow an author
// -----------------------------------------------------------------------------
export function followAuthor(targetAuthor, followingAuthor) {
  return async (dispatch, getState) => {
    let response = {}
    try {
      // One follow row per pair, so a second follow (another tab) is refused instead of counted
      // twice by gnjAmplifyCounters (security sprint PR 5). Older rows keep their random IDs.
      response = (
        await API.graphql(
          graphqlOperation(custom_mutations.createFollowing, {
            input: {
              id: `${followingAuthor.id}#${targetAuthor.id}`,
              user: followingAuthor.id,
              following: targetAuthor.id,
            },
          })
        )
      ).data.createFollowing
      createNotificationFromFollow(targetAuthor.id, getState().user.userData)
    } catch (err) {
      if (alreadyExists(err)) {
        response = { id: `${followingAuthor.id}#${targetAuthor.id}` }
      } else {
        console.log('error following author: ', err)
      }
    }
    return response
  }
}

// Following and follower counts to show after a follow (+1) or unfollow (-1). The stored
// counts are kept by gnjAmplifyCounters from the Following stream (security sprint PR 5), so
// nothing is written here.
// -----------------------------------------------------------------------------
export function adjustCounts(targetAuthor, followingAuthor, difference) {
  return async () => {
    const add = (count) => Math.max(0, (count || 0) + difference)
    return {
      user: { ...followingAuthor, followingCount: add(followingAuthor.followingCount) },
      author: { ...targetAuthor, followerCount: add(targetAuthor.followerCount) },
    }
  }
}

// Unfollow an author
// -----------------------------------------------------------------------------
export function unfollowAuthor(followingId) {
  return async () => {
    try {
      await API.graphql(
        graphqlOperation(custom_mutations.deleteFollowing, {
          input: { id: followingId },
        })
      )
    } catch (err) {
      console.log('error unfollowAuthor: ', err)
    }
  }
}

// Following
// -----------------------------------------------------------------------------
export function doesSourceFollowTarget(sourceUserId, targetUserId) {
  return async () => {
    try {
      const data = await API.graphql(
        graphqlOperation(custom_queries.isUserFollowingTheAuthor, {
          following: targetUserId,
          user: sourceUserId,
        })
      )
      return data.data.isUserFollowingTheAuthor
    } catch (err) {
      console.log('error getting doesSourceFollowTarget: ', err)
    }
  }
}

export function getUserFollowings(userId, nextToken) {
  return async () => {
    try {
      const data = await API.graphql(
        graphqlOperation(custom_queries.userFollowingAsKey, {
          user: userId,
          nextToken: nextToken,
          limit: 30,
        })
      )
      return data.data.userFollowingAsKey
    } catch (err) {
      console.log('error getUserFollowings()', err)
    }
  }
}

export function getUserFollowers(userId, nextToken) {
  return async () => {
    try {
      const data = await API.graphql(
        graphqlOperation(custom_queries.userFollowerAsKey, {
          following: userId,
          nextToken: nextToken,
          limit: 30,
        })
      )
      return data.data.userFollowerAsKey
    } catch (err) {
      console.log('error getUserFollowers()', err)
    }
  }
}

export function getChallengeData(username, limit, nextToken) {
  return async () => {
    try {
      const currentYear = new Date().getFullYear()
      const currentMonth = new Date().getMonth()
      if (username) {
        const data = await API.graphql(
          graphqlOperation(custom_queries.yearMonthUserAsKey, {
            user: { eq: username },
            yearMonth: currentYear + '-' + ('0' + (currentMonth + 1)).slice(-2),
            sortDirection: 'DESC',
          })
        )
        return data.data.yearMonthUserAsKey
      } else {
        const data = await API.graphql(
          graphqlOperation(custom_queries.yearMonthJournalsPerMonthAsKey, {
            yearMonth: currentYear + '-' + ('0' + (currentMonth + 1)).slice(-2),
            nextToken: nextToken,
            limit,
            sortDirection: 'DESC',
          })
        )
        return data.data.yearMonthJournalsPerMonthAsKey
      }
    } catch (err) {
      console.log('error getChallengeData()', err)
    }
  }
}

export function setChallengeLimit(limit) {
  return (dispatch) => {
    dispatch({ type: 'SET_CHALLENGE_LIMIT', limit })
  }
}

// Update user displayName
// -----------------------------------------------------------------------------
export function updateUserSetting(userData) {
  return async (dispatch) => {
    let user = await Auth.currentAuthenticatedUser()
    let userId = user.signInUserSession.idToken.payload['cognito:username']
    try {
      let data = await Auth.updateUserAttributes(user, userData)
      await API.graphql(
        graphqlOperation(custom_mutations.updateUser, {
          input: { id: userId, displayName: userData.preferred_username },
        })
      )
      dispatch(getCurrentUser())
      return data
    } catch (err) {
      console.log('error updating user setting: ', err)
    }
  }
}

export function adjustUserJournalCount(diff) {
  return async (dispatch, getState) => {
    try {
      let total = getState().user.userData.totalJournal + diff
      if (total < 0) total = 0
      await API.graphql(
        graphqlOperation(custom_mutations.updateUser, {
          input: { id: getState().user.userData.id, totalJournal: total },
        })
      )
      if (total % 30 === 0 && total !== 0) {
        createNotificationThirtyJournal(total, getState().user.userData.id)
      }
      dispatch({ type: 'ADJUST_TOTAL_JOURNAL', total })
    } catch (e) {
      console.log(e)
    }
  }
}

// Delete my account
// -----------------------------------------------------------------------------
// The server cancels any subscription, disables the account and signs it out
// everywhere, then deletes its data once the grace period is over. The ID token
// lets it find the account's own uploads (public/<identity id>/...). Throws if
// the request failed, in which case nothing was disabled.
export function requestAccountDeletion() {
  return async (dispatch) => {
    const session = await Auth.currentSession()
    const result = await API.graphql(
      graphqlOperation(custom_mutations.gnjAmplifyRequestAccountDeletion, {
        idToken: session.idToken.toString() || null,
      })
    )
    await dispatch(userSignOut())
    return result
  }
}

// Update bookOrder under User table
// -----------------------------------------------------------------------------
export function updateUserBookOrder(newBookOrder) {
  return async (dispatch) => {
    let user = await Auth.currentAuthenticatedUser()
    let userId = user.signInUserSession.idToken.payload['cognito:username']
    try {
      await API.graphql(
        graphqlOperation(custom_mutations.updateUser, {
          input: { id: userId, bookOrder: newBookOrder },
        })
      )
      dispatch(getCurrentUser())
    } catch (err) {
      console.log('error updating userBooks data: ', err)
    }
  }
}
